import { useState, type FormEvent } from 'react';
import { Button, ErrorNotice } from '@hive/ui';
import { api, errorMessage, type VenueImportResult } from '../api/client';
import { Dialog } from './Dialog';
import { emptyVenueForm, VenueFieldsForm, venueNumbers, type VenueForm } from './VenueFields';

/** Trimmed text, left out when empty (for create requests). */
const t = (s: string) => (s.trim() ? s.trim() : undefined);

export function AddVenueDialog({ onClose, onCreated }: { onClose(): void; onCreated(venueId: string): void }) {
  const [form, setForm] = useState<VenueForm>(emptyVenueForm);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const nums = venueNumbers(form);
    if (nums.error) return setError(nums.error);
    if (!nums.capacity) return setError('Give the licensed capacity: the number of people the venue is licensed for.');
    setBusy(true);
    setError(null);
    const { data, error: err } = await api.POST('/v1/setup/venues', {
      body: {
        name: form.name.trim(),
        timezone: form.timezone.trim(),
        code: t(form.code),
        externalRef: t(form.externalRef),
        area: t(form.area),
        city: t(form.city),
        country: t(form.country),
        address: t(form.address),
        lat: nums.lat,
        lng: nums.lng,
        capacity: nums.capacity,
      },
    });
    setBusy(false);
    if (!data) return setError(errorMessage(err));
    onCreated(data.venue.id);
  }

  return (
    <Dialog
      titleId="add-venue-title"
      title="Add a venue"
      subtitle="The venue and its licensed capacity. The venue (or its installer) sets up its own screens in Keeper."
      onClose={onClose}
      width={640}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form="add-venue-form" disabled={busy}>
            {busy ? 'Adding…' : 'Add venue'}
          </Button>
        </>
      }
    >
      <form id="add-venue-form" className="mc-form" onSubmit={submit}>
        {error && <ErrorNotice title={error} />}
        <VenueFieldsForm form={form} onChange={setForm} capacityRequired />
        <p className="mc-note mc-note--info">Screens are added and named by the venue or its installer in Keeper (Screens and areas). You can also add them from the venue's setup page.</p>
      </form>
    </Dialog>
  );
}

const TEMPLATE = 'name,code,externalRef,area,city,country,timezone,lat,lng,capacity,screens\nThe Local Tap,LT-DXB-001,CRM-1042,JLT,Dubai,UAE,Asia/Dubai,25.0693,55.1418,180,3\n';

/** Paste or choose a CSV, preview what it would do, then import. */
export function ImportVenuesDialog({ onClose, onImported }: { onClose(): void; onImported(): void }) {
  const [csv, setCsv] = useState('');
  const [preview, setPreview] = useState<VenueImportResult | null>(null);
  const [result, setResult] = useState<VenueImportResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run(dryRun: boolean) {
    setBusy(true);
    setError(null);
    const { data, error: err } = await api.POST('/v1/setup/venues/import', { body: { csv, dryRun } });
    setBusy(false);
    if (!data) return setError(errorMessage(err));
    if (dryRun) setPreview(data);
    else {
      setResult(data);
      onImported();
    }
  }

  const shown = result ?? preview;
  return (
    <Dialog
      titleId="import-title"
      title="Import venues from CSV"
      subtitle="Rows are matched to existing venues by CRM reference, then venue ID."
      onClose={onClose}
      width={760}
      footer={
        result ? (
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button onClick={() => run(true)} disabled={busy || !csv.trim()}>
              Preview
            </Button>
            <Button variant="primary" onClick={() => run(false)} disabled={busy || !preview || preview.created + preview.updated === 0}>
              {preview ? `Import ${preview.created + preview.updated} venue${preview.created + preview.updated === 1 ? '' : 's'}` : 'Import'}
            </Button>
          </>
        )
      }
    >
      <div className="mc-form">
        {error && <ErrorNotice title={error} />}
        {!result && (
          <>
            <div className="mc-row-between">
              <label className="hv-btn hv-btn--secondary hv-btn--sm">
                Choose a file
                <input
                  type="file"
                  accept=".csv,text/csv"
                  className="hv-sr-only"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setCsv(await file.text());
                      setPreview(null);
                    }
                  }}
                />
              </label>
              <Button variant="link" onClick={() => (setCsv(TEMPLATE), setPreview(null))}>
                Start from the template
              </Button>
            </div>
            <textarea
              aria-label="CSV"
              className="hv-input mc-textarea"
              placeholder="name,code,externalRef,city,country,timezone,lat,lng,screens"
              value={csv}
              onChange={(e) => (setCsv(e.target.value), setPreview(null))}
              spellCheck={false}
            />
            <p className="mc-small hv-muted">
              Columns: name (required); timezone and capacity, the licensed number of people (required for new venues); code, externalRef,
              area, city, country, address, lat, lng; screens (how many to create for a new venue, optional: the venue can set them up).
            </p>
          </>
        )}
        {shown && (
          <>
            <p className={`mc-note ${shown.failed ? 'mc-note--bad' : 'mc-note--ok'}`}>
              {result ? 'Imported: ' : 'This would '}
              {[
                shown.created && `${result ? '' : 'add '}${shown.created} new`,
                shown.updated && `${result ? '' : 'update '}${shown.updated} ${result ? 'updated' : ''}`.trim(),
                shown.unchanged && `${shown.unchanged} unchanged`,
                shown.failed && `${shown.failed} with problems`,
              ]
                .filter(Boolean)
                .join(', ')}
              .
            </p>
            <table className="mc-table mc-table--compact">
              <thead>
                <tr>
                  <th scope="col">Line</th>
                  <th scope="col">Venue</th>
                  <th scope="col">Result</th>
                  <th scope="col">Detail</th>
                </tr>
              </thead>
              <tbody>
                {shown.rows.map((r) => (
                  <tr key={r.line}>
                    <td>{r.line}</td>
                    <td>{r.name ?? '—'}</td>
                    <td className={r.action === 'error' ? 'mc-bad' : r.action === 'unchanged' ? 'hv-muted' : 'mc-good'}>
                      {{ create: 'New', update: 'Update', unchanged: 'No change', error: 'Problem' }[r.action]}
                    </td>
                    <td className="mc-small">{r.message ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </Dialog>
  );
}
